import { useState } from "react";
import type { BuyerResearchRunSummaryDto } from "@/api/buyerResearch";
import { extractErrorMessage } from "@/api/client";
import { Button, Input } from "@/design-system/components/ui";
import { researchCopy } from "./researchCopy";
import { useStartQuickSearch } from "./useBuyerResearch";

/** The server's own bound on a typed quick search. */
export const QUICK_SEARCH_MAX_LENGTH = 200;

/** How many characters of the typed sentence a run label shows before "…". */
export const QUICK_LABEL_MAX = 40;

/**
 * The typed sentence cut for a label: on a word where one falls in the last
 * third of the budget, with "…" when anything was cut. The full sentence goes
 * in a Tooltip beside it — never in a native `title`.
 */
export function truncateQuickText(text: string, max: number = QUICK_LABEL_MAX): string {
  const t = text.trim().replace(/\s+/g, " ");
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const space = cut.lastIndexOf(" ");
  const head = space >= Math.floor((max * 2) / 3) ? cut.slice(0, space) : cut;
  return `${head.trimEnd()}…`;
}

/** True when a run summary is a typed Quick search. */
export function isQuickRun(run: Pick<BuyerResearchRunSummaryDto, "kind"> | null | undefined): boolean {
  return run?.kind === "Quick";
}

/**
 * A1/A2 — the Quick search box. One line with its own QUICK SEARCH button;
 * Enter does the same. The server reads the sentence and queues the run, and
 * the new run is selected at once.
 *
 * <p>The button is not decoration. Without it the only button beside the box
 * was the full research action, which read as the box's submit — a specialist
 * typed a sentence and pressed it, and got a full run from the brief instead.
 * The two searches now each have their own named button.</p>
 *
 * <p>🔴 <b>A 400 creates nothing.</b> The server refuses a sentence it cannot
 * read with a plain message, and that message is shown here verbatim
 * (`role="alert"`) — no run is selected because none exists.</p>
 *
 * <p>Rendered only for a reader who can edit the buyer, exactly as RESEARCH
 * LISTINGS is — the caller decides, so this component never renders a control
 * the server would refuse.</p>
 */
export function QuickSearchBox({
  buyerLeadId,
  onStarted,
}: {
  buyerLeadId: string;
  onStarted: (run: BuyerResearchRunSummaryDto) => void;
}) {
  const copy = researchCopy.quick;
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const start = useStartQuickSearch(buyerLeadId);

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed || start.isPending) return;
    setError(null);
    start.mutate(trimmed, {
      onSuccess: (run) => {
        setText("");
        onStarted(run);
      },
      onError: (e: unknown) => setError(extractErrorMessage(e)),
    });
  };

  return (
    <div className="grid gap-space-1 min-w-0 w-full max-w-[32rem]" data-testid="research-quick-search">
      <form
        role="search"
        aria-label={copy.label}
        className="flex items-start gap-space-2 min-w-0"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Input
          className="min-w-0 flex-1"
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={copy.placeholder}
          aria-label={copy.label}
          maxLength={QUICK_SEARCH_MAX_LENGTH}
          disabled={start.isPending}
          enterKeyHint="search"
          data-testid="research-quick-search-input"
        />
        <Button
          type="submit"
          variant="secondary"
          className="shrink-0"
          loading={start.isPending}
          disabled={!text.trim()}
          data-testid="research-quick-search-submit"
        >
          {start.isPending ? copy.submitPending : copy.submit}
        </Button>
      </form>
      {start.isPending && (
        <p className="m-0 text-caption text-gray-mid" role="status" data-testid="research-quick-search-pending">
          {copy.pendingHint}
        </p>
      )}
      {error && (
        <p className="m-0 text-caption text-status-error break-words" role="alert" data-testid="research-quick-search-error">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * How the server read a quick search — its read line and each note — shown
 * where an ordinary run shows its brief, and shown at once rather than behind
 * the collapsed account.
 */
export function QuickSearchReadLine({ run }: { run: BuyerResearchRunSummaryDto }) {
  const qs = run.quickSearch;
  if (!qs) return null;
  return (
    <section className="grid gap-space-1" data-testid="research-quick-read">
      <h3 className="m-0 text-caption uppercase tracking-wide text-gray-mid">
        {researchCopy.quick.readHeading}
      </h3>
      <p className="m-0 text-small text-onyx break-words" data-testid="research-quick-read-line">
        {qs.readLine}
      </p>
      {qs.notes.map((note, i) => (
        <p key={i} className="m-0 text-caption text-gray-mid break-words" data-testid="research-quick-read-note">
          {note}
        </p>
      ))}
    </section>
  );
}
